import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save,
  X,
  Key,
  ExternalLink,
  Info,
  CheckCircle2,
  ShoppingBag,
  Loader2,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  get_restaurant_setting,
  save_restaurant_setting,
  server_post_data,
} from "../ServiceConnection/serviceconnection";

// platform_connect values:
// 2 = zomato, 3 = zomato-dine, 4 = swiggy, 5 = swiggy-dine
const PLATFORMS = [
  {
    id: "zomato",
    platform_connect: "2",
    label: "Zomato",
    description: "Zomato delivery integration",
    color: "destructive",
    docsUrl: "https://www.zomato.com/partners",
    docsLabel: "Zomato Partners Dashboard",
    fields: ["mapping_code", "api_key"],
  },
  {
    id: "zomato_dine",
    platform_connect: "3",
    label: "Zomato Dine-in",
    description: "Zomato dine-in integration",
    color: "destructive",
    docsUrl: "https://www.zomato.com/partners",
    docsLabel: "Zomato Partners Dashboard",
    fields: ["mapping_code", "api_key"],
  },
  {
    id: "swiggy",
    platform_connect: "4",
    label: "Swiggy",
    description: "Swiggy delivery integration",
    color: "warning",
    docsUrl: "https://partner.swiggy.com/",
    docsLabel: "Swiggy Partner Portal",
    fields: ["mapping_code", "api_key"],
  },
  {
    id: "swiggy_dine",
    platform_connect: "5",
    label: "Swiggy Dine-in",
    description: "Swiggy dine-in integration",
    color: "warning",
    docsUrl: "https://partner.swiggy.com/",
    docsLabel: "Swiggy Partner Portal",
    // 4 fields as per the Swiggy Dine mapping spec
    fields: ["mapping_code", "api_key", "app_secret", "access_token"],
  },
];

// Human-readable labels & placeholders per field key
const FIELD_META = {
  mapping_code: {
    label: "Mapping Code / RID",
    placeholder: "e.g. 489091 (partner_restaurant_id)",
    type: "text",
    icon: false,
  },
  api_key: {
    label: "API Key (Auth ID / x-api-key)",
    placeholder: "e.g. 9789789e-a66d-4643-...",
    type: "password",
    icon: true,
  },
  app_secret: {
    label: "App Secret (Secret Key)",
    placeholder: "e.g. ddbaef62-9750-4dae-...",
    type: "password",
    icon: true,
  },
  access_token: {
    label: "Access Token (Auth-key)",
    placeholder: "e.g. 3057e00e-3456-41e9-...",
    type: "password",
    icon: true,
  },
};

// Build an empty fields object for a given platform
const emptyFields = (platform) =>
  Object.fromEntries(platform.fields.map((f) => [f, ""]));

export default function ApiKeySetting() {
  const { toast } = useToast();

  const [formData, setFormData] = useState(() =>
    Object.fromEntries(
      PLATFORMS.map((p) => [p.platform_connect, emptyFields(p)]),
    ),
  );
  const [originalData, setOriginalData] = useState(() =>
    Object.fromEntries(
      PLATFORMS.map((p) => [p.platform_connect, emptyFields(p)]),
    ),
  );
  const [savingPlatform, setSavingPlatform] = useState(null);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const [mappingKey, setMappingKey] = useState("");

  const isDirty = useCallback(
    (pc) => JSON.stringify(formData[pc]) !== JSON.stringify(originalData[pc]),
    [formData, originalData],
  );

  const isConfigured = useCallback(
    (pc) => Object.values(originalData[pc] || {}).some((v) => v.trim() !== ""),
    [originalData],
  );

  const handleFieldChange = useCallback((platform_connect, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [platform_connect]: { ...prev[platform_connect], [field]: value },
    }));
  }, []);

  const fetchSettingsData = useCallback(async () => {
    setLoadingFetch(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 0);
    fd.append("platform_api_keys", 1);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);
      if (!response.data.error) {
        const rows = response.data.message.data_api_keys || [];
        const data_platform_codes =
          response.data.message.data_platform_codes || [];

        const newData = Object.fromEntries(
          PLATFORMS.map((p) => [p.platform_connect, emptyFields(p)]),
        );

        setMappingKey(data_platform_codes[0]?.mapping_code ?? "");

        rows.forEach((row) => {
          const pc = String(row.platform_connect);
          if (newData[pc] !== undefined) {
            const platform = PLATFORMS.find((p) => p.platform_connect === pc);
            const entry = {};
            platform.fields.forEach((f) => {
              entry[f] = row[f] || "";
            });
            newData[pc] = entry;
          }
        });

        setFormData(newData);
        setOriginalData(structuredClone(newData));
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to load API key settings",
        variant: "destructive",
      });
    } finally {
      setLoadingFetch(false);
    }
  }, [toast]);

  const handleSave = useCallback(
    async (platform) => {
      const fields = formData[platform.platform_connect];
      const hasAny = Object.values(fields).some((v) => v.trim() !== "");
      if (!hasAny) {
        toast({
          title: "Validation Error",
          description: `Please enter at least one field for ${platform.label}`,
          variant: "destructive",
        });
        return;
      }

      setSavingPlatform(platform.platform_connect);
      const fd = new FormData();
      fd.append("from_where", "5");
      fd.append("platform_connect", platform.platform_connect);

      // Append all fields for this platform
      platform.fields.forEach((f) => {
        fd.append(f, fields[f] || "");
      });

      try {
        const response = await server_post_data(save_restaurant_setting, fd);
        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          toast({
            title: "Success",
            description: `${platform.label} API keys saved successfully`,
          });
          await fetchSettingsData();
        }
      } catch {
        toast({
          title: "Error",
          description: "Failed to save API keys",
          variant: "destructive",
        });
      } finally {
        setSavingPlatform(null);
      }
    },
    [formData, toast, fetchSettingsData],
  );

  const handleCancel = useCallback(
    (pc) => {
      setFormData((prev) => ({
        ...prev,
        [pc]: structuredClone(originalData[pc]),
      }));
    },
    [originalData],
  );

  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  const totalConfigured = PLATFORMS.filter((p) =>
    isConfigured(p.platform_connect),
  ).length;

  if (loadingFetch) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">
          Loading API key settings...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Platform API Keys
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Manage API keys for food delivery platform integrations
        </p>
      </div>

      {/* Uprestro Mapping Code */}
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Uprestro Mapping Code
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Share this code with your delivery platform partners to link your
          Uprestro account
        </p>
        {mappingKey && (
          <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted border text-sm font-mono font-medium text-foreground">
            <Key className="w-3.5 h-3.5 text-muted-foreground" />
            {mappingKey}
          </div>
        )}
      </div>

      {/* Overall status */}
      {totalConfigured > 0 && (
        <Alert className="border-success/50 bg-success/10">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            {totalConfigured} of {PLATFORMS.length} platform
            {totalConfigured > 1 ? "s" : ""} configured and active
          </AlertDescription>
        </Alert>
      )}

      {/* Platform Cards */}
      {PLATFORMS.map((platform) => {
        const pc = platform.platform_connect;
        const fields = formData[pc];
        const dirty = isDirty(pc);
        const configured = isConfigured(pc);
        const isSaving = savingPlatform === pc;
        const colorCls =
          platform.color === "destructive"
            ? "text-destructive bg-destructive/10"
            : "text-warning bg-warning/10";
        const iconCls =
          platform.color === "destructive"
            ? "text-destructive"
            : "text-warning";

        return (
          <Card key={pc} className={dirty ? "border-warning/40" : ""}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorCls}`}
                  >
                    <ShoppingBag className={`w-5 h-5 ${iconCls}`} />
                  </div>
                  <div>
                    <CardTitle className="text-base">
                      {platform.label}
                    </CardTitle>
                    <CardDescription>{platform.description}</CardDescription>
                  </div>
                </div>
                {configured && !dirty && (
                  <CheckCircle2 className="w-5 h-5 text-success" />
                )}
                {dirty && (
                  <span className="text-xs text-warning font-medium px-2 py-1 rounded-full bg-warning/10 border border-warning/20">
                    Unsaved
                  </span>
                )}
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Render fields in a 2-col grid; on platforms with 4 fields the grid naturally wraps */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {platform.fields.map((fieldKey) => {
                  const meta = FIELD_META[fieldKey];
                  return (
                    <div key={fieldKey} className="space-y-2">
                      <Label>{meta.label}</Label>
                      <div className="relative">
                        {meta.icon && (
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        )}
                        <Input
                          type={meta.type}
                          placeholder={meta.placeholder}
                          value={fields[fieldKey] ?? ""}
                          onChange={(e) =>
                            handleFieldChange(pc, fieldKey, e.target.value)
                          }
                          className={meta.icon ? "pl-9" : ""}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs text-muted-foreground">
                Get your credentials from{" "}
                <a
                  href={platform.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  {platform.docsLabel}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </p>

              {/* Per-card action buttons */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCancel(pc)}
                  disabled={isSaving || !dirty}
                  className="gap-2"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSave(platform)}
                  disabled={isSaving || !dirty}
                  className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-3 h-3" />
                      Save
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Security Note */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Security Note:</strong> Your API keys are encrypted and stored
          securely. Each platform is saved independently — configure only the
          platforms you use.
        </AlertDescription>
      </Alert>
    </div>
  );
}
